import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { toast } from "react-toastify";

import ShareChannelIcons from "../../ShareModal/ShareChannelIcons";
import WheelQRCode from "../../ShareModal/WheelQRCode";
import { downloadBlob, downloadPng, downloadSvg } from "../../ShareModal/qrDownload";
import { WHEEL_SHARE_IMAGE_FALLBACK } from "../../ShareModal/toAbsoluteMediaUrl";
import { copyTextToClipboard } from "../../WheelCollaboration/inviteLink";
import { tokens } from "../../../theme/tokens";

const { brand, field, radius, spacing } = tokens;

/**
 * T22.774 (Gordon 2026-10-29, decision D4, UX audit T36.6 #4): Share Gallery
 * used to only copy a link (or open the OS sheet) — no dialog at all, while a
 * wheel's Share offers QR, PDF, embed and social. This is the Gallery's share
 * dialog with the same parts: copy link, social targets, device share, a QR
 * code (SVG/PNG), a one-page PDF poster and an embed snippet.
 *
 * ONE link (D4, T36.6 #5): the link shown in the box, the one the QR encodes,
 * the one Copy writes and the one every social target / the PDF / the embed
 * carry are all `url` — the backend gallery share URL (per-owner preview card
 * for crawlers, redirect to the Gallery page for people; see GalleryHeader).
 *
 * The embed is a plain link card (an <a> the site owner pastes), not an
 * iframe: there is no embeddable Gallery page on the backend, and a link card
 * needs no framing permission to work on any website.
 */

export const GALLERY_COPIED_TOAST = "Gallery link copied!";
const COPY_BLOCKED =
  "Your browser blocked copying. Select the link and copy it manually.";

const Section = styled.section`
  padding: ${spacing.lg}px 0;
  border-top: 1px solid #ececec;
  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
`;

const Label = styled.h3`
  margin: 0 0 ${spacing.sm}px;
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${field.muted};
`;

const Row = styled.div`
  display: flex;
  gap: ${spacing.sm}px;
  align-items: stretch;
`;

const LinkField = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 44px;
  box-sizing: border-box;
  padding: 0 ${spacing.md}px;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  color: ${field.ink};
  background: ${field.bgDisabled};
  border: 1px solid ${field.border};
  border-radius: ${radius.sm}px;
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto;
  min-height: 44px;
  min-width: 44px;
  padding: 0 ${spacing.md}px;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  border-radius: ${radius.sm}px;
  cursor: pointer;
  color: ${({ $primary }) => ($primary ? "#fff" : field.ink)};
  background: ${({ $primary }) => ($primary ? brand.primary : "#fff")};
  border: 1px solid ${({ $primary }) => ($primary ? brand.primary : field.border)};
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const Icons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm}px;
  & > * {
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const Note = styled.p`
  margin: ${spacing.sm}px 0 0;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: ${field.muted};
`;

const QrBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.lg}px;
  align-items: center;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  flex: 1 1 160px;
`;

const Code = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  padding: ${spacing.sm}px;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  color: ${field.ink};
  border: 1px solid ${field.border};
  border-radius: ${radius.sm}px;
  resize: vertical;
`;

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** The copy-paste embed: a link card to the Gallery, no iframe needed. */
export function buildGalleryEmbedSnippet(url) {
  if (!url) return "";
  return (
    `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" ` +
    `style="display:inline-block;padding:12px 20px;border-radius:8px;` +
    `background:#9B5AF8;color:#fff;font-family:sans-serif;font-weight:600;` +
    `text-decoration:none">See this Gallery on iYuga</a>`
  );
}

const GalleryShareModal = ({ open, url, onClose, title = "iYuga Gallery" }) => {
  const fieldRef = useRef(null);
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copyBlocked, setCopyBlocked] = useState(false);
  const [pngBusy, setPngBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [embedCopied, setEmbedCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setCopied(false);
      setCopyBlocked(false);
      setPdfError("");
      setEmbedCopied(false);
    }
  }, [open]);

  const stem = "iyuga-gallery";
  const embed = buildGalleryEmbedSnippet(url);

  const selectLink = () => {
    const el = fieldRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    if (await copyTextToClipboard(url)) {
      setCopyBlocked(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(GALLERY_COPIED_TOAST, { toastId: "gallery-share-url-copied" });
      // T22.736: a successful Copy link ends the flow (success toast, dialog closes).
      onClose?.();
      return;
    }
    // T22.736: a blocked clipboard never dead-ends — the link stays on screen,
    // selected, to copy by hand.
    setCopyBlocked(true);
    selectLink();
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, url });
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
  };

  const handleSvg = () => {
    try {
      downloadSvg(qrRef.current, `${stem}-qr.svg`);
    } catch (e) {
      toast.error("Couldn't export the SVG.");
    }
  };

  const handlePng = async () => {
    if (pngBusy) return;
    setPngBusy(true);
    try {
      await downloadPng(qrRef.current, `${stem}-qr.png`, 1024);
    } catch (e) {
      toast.error("Couldn't export the PNG.");
    } finally {
      setPngBusy(false);
    }
  };

  const handlePdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    setPdfError("");
    try {
      const { renderWheelPdf } = await import(
        "../../ShareModal/pdfExport/renderPdfPages"
      );
      const plan = {
        title,
        filenameStem: stem,
        pages: [
          {
            kind: "cover",
            title,
            subtitle: "Scan the code or open the link to see the wheels.",
            url,
            footer: "iYuga",
          },
        ],
      };
      const blob = await renderWheelPdf(plan, { wheelNode: qrRef.current });
      downloadBlob(blob, `${stem}.pdf`);
    } catch (err) {
      setPdfError("We couldn't build the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const handleCopyEmbed = async () => {
    if (await copyTextToClipboard(embed)) {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
      toast.success("Embed code copied to clipboard!", {
        toastId: "gallery-embed-code-copied",
      });
    } else {
      toast.error("Couldn't copy — select the code and copy manually.");
    }
  };

  const nativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="gallery-share-title"
      fullWidth
      maxWidth="sm"
      scroll="paper"
      TransitionProps={{ onEntered: selectLink }}
    >
      <DialogTitle
        id="gallery-share-title"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Share Gallery
        <IconButton aria-label="Close" onClick={onClose} sx={{ width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Section aria-label="Copy link">
          <Label>Copy link</Label>
          <Row>
            <LinkField
              ref={fieldRef}
              readOnly
              value={url || ""}
              aria-label="Gallery link"
              data-testid="gallery-share-url"
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
            />
            <Btn type="button" $primary onClick={handleCopy} aria-label="Copy link">
              {copied ? (
                <CheckIcon style={{ fontSize: 18 }} />
              ) : (
                <ContentCopyIcon style={{ fontSize: 18 }} />
              )}
              <span aria-hidden="true">{copied ? "Copied" : "Copy"}</span>
            </Btn>
          </Row>
          {copyBlocked && <Note role="status">{COPY_BLOCKED}</Note>}
        </Section>

        <Section aria-label="Share to">
          <Label>Share to</Label>
          <Icons>
            <ShareChannelIcons
              url={url}
              quote="Check out this Gallery on iYuga!"
              description="Check out this Gallery on iYuga!"
              media={WHEEL_SHARE_IMAGE_FALLBACK}
              onCopy={handleCopy}
            />
          </Icons>
          {nativeShare && (
            <Btn
              type="button"
              onClick={handleNativeShare}
              style={{ marginTop: spacing.sm, width: "100%" }}
            >
              Share via device
            </Btn>
          )}
        </Section>

        <Section aria-label="Gallery QR code">
          <Label>QR code</Label>
          <QrBlock>
            <div data-testid="gallery-qr">
              <WheelQRCode
                ref={qrRef}
                url={url}
                size={160}
                idPrefix="gallery-qr"
                label="QR code for this Gallery"
              />
            </div>
            <Stack>
              <Btn type="button" onClick={handleSvg} aria-label="Download QR code as SVG">
                <DownloadRoundedIcon style={{ fontSize: 18 }} />
                <span>SVG</span>
              </Btn>
              <Btn
                type="button"
                onClick={handlePng}
                disabled={pngBusy}
                aria-busy={pngBusy}
                aria-label="Download QR code as PNG"
              >
                <DownloadRoundedIcon style={{ fontSize: 18 }} />
                <span>{pngBusy ? "Preparing…" : "PNG"}</span>
              </Btn>
            </Stack>
          </QrBlock>
        </Section>

        <Section aria-label="Download as PDF">
          <Label>Download as PDF</Label>
          <Note style={{ marginTop: 0 }}>
            A one-page PDF with the QR code and the link, ready to print or email.
          </Note>
          <Btn
            type="button"
            onClick={handlePdf}
            disabled={pdfBusy}
            aria-busy={pdfBusy}
            aria-label="Download this Gallery as a PDF"
            style={{ marginTop: spacing.sm, width: "100%" }}
          >
            <PictureAsPdfOutlinedIcon style={{ fontSize: 18 }} />
            <span>{pdfBusy ? "Preparing PDF…" : "Download PDF"}</span>
          </Btn>
          {pdfError && <Note role="alert">{pdfError}</Note>}
        </Section>

        <Section aria-label="Embed">
          <Label>Embed</Label>
          <Note style={{ marginTop: 0 }}>
            Paste this on your website to link to your Gallery.
          </Note>
          <Code
            readOnly
            value={embed}
            aria-label="Gallery embed code"
            onFocus={(e) => e.target.select()}
            style={{ marginTop: spacing.sm }}
          />
          <Btn
            type="button"
            onClick={handleCopyEmbed}
            style={{ marginTop: spacing.sm }}
            aria-label="Copy embed code"
          >
            {embedCopied ? "Copied" : "Copy code"}
          </Btn>
        </Section>
      </DialogContent>
    </Dialog>
  );
};

GalleryShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  url: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default GalleryShareModal;
